import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import { GRAMS_PER_TOLA, formatMoney } from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";

/**
 * /pakistan/zakat-nisab — Live nisab threshold calculator.
 *
 * Nisab (per classical fiqh): the minimum wealth on which zakat becomes
 * obligatory. Two thresholds:
 *   - Gold nisab: 7.5 tola (≈87.48 g) of 24K gold
 *   - Silver nisab: 52.5 tola (≈612.35 g) of silver
 * The lower one is used by most contemporary scholars to protect the
 * poor (they qualify for zakat sooner and pay less).
 *
 * Zakat rate: 2.5% of total zakatable wealth held for one lunar year.
 * Silver spot: we approximate as gold_spot / 80 (long-run ratio) since
 * we don't fetch silver directly on this route; that is disclosed.
 */

const GOLD_NISAB_TOLA = 7.5;
const SILVER_NISAB_TOLA = 52.5;
const CANONICAL = "https://drhint.com/pakistan/zakat-nisab";

export const Route = createFileRoute("/pakistan/zakat-nisab")({
  head: () => ({
    meta: [
      { title: "Zakat Nisab Pakistan 2026 — Live Gold & Silver Threshold in PKR" },
      {
        name: "description",
        content:
          "Live nisab threshold in PKR — 7.5 tola gold and 52.5 tola silver — plus 2.5% zakat calculator. Updated every 10 minutes from international spot + USD/PKR rate.",
      },
      { property: "og:title", content: "Zakat Nisab Pakistan — Live PKR Threshold" },
      {
        property: "og:description",
        content:
          "Live gold and silver nisab in PKR + 2.5% zakat calculator for Pakistan.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  component: ZakatNisabPage,
});

function ZakatNisabPage() {
  const { data } = useGoldRates();
  const [wealth, setWealth] = useState<string>("0");

  const perGramGoldPkr = (data.spotUsdPerOz / 31.1035) * data.pkrPerUsd;
  // Approximate silver from gold using long-run 80:1 ratio (disclosed below).
  const perGramSilverPkr = perGramGoldPkr / 80;

  const goldNisabPkr = perGramGoldPkr * GOLD_NISAB_TOLA * GRAMS_PER_TOLA;
  const silverNisabPkr = perGramSilverPkr * SILVER_NISAB_TOLA * GRAMS_PER_TOLA;
  const effectiveNisab = Math.min(goldNisabPkr, silverNisabPkr);

  const wealthNum = Math.max(0, Number(wealth) || 0);
  const zakatDue = wealthNum >= effectiveNisab ? wealthNum * 0.025 : 0;
  const qualifies = wealthNum >= effectiveNisab;

  const faqs = [
    {
      q: "Nisab kya hai aur kaunsa use karna chahiye?",
      a: "Nisab wo minimum wealth threshold hai jis par zakat farz hoti hai. Gold nisab = 7.5 tola (24K), silver nisab = 52.5 tola. Majority contemporary scholars silver nisab prefer karte hain kyunki wo lower hai — is se zyada log zakat pay kar sakte hain aur zyada log qualify hote hain zakat receive karne ke liye.",
    },
    {
      q: "2.5% zakat kis wealth par apply hoti hai?",
      a: "Cash, bank balance, savings, business inventory, gold/silver jewellery (personal use ke ilawa madhab ke hisaab se), stocks/mutual funds, aur receivable loans. Primary residence, personal car aur daily-use items exempt hain. Wealth ek lunar year (Hijri) tak nisab ke upar honi chahiye.",
    },
    {
      q: "Aap silver ka rate kaise nikaal rahe hain?",
      a: "Ye page primarily gold spot fetch karta hai. Silver estimate long-run gold/silver ratio (~80:1) se ata hai — real-time silver spot se 2–5% variation ho sakta hai. Exact silver-based nisab ke liye local sarafa bazaar ka aaj ka silver rate check karen.",
    },
    {
      q: "Ramadan mein zakat dena zaroori hai?",
      a: "Nahi — zakat aapke personal Hijri anniversary date par due hoti hai (jab wealth pehli baar nisab par pahunchi). Log Ramadan choose karte hain double reward ke liye, lekin ye religious obligation nahi hai.",
    },
    {
      q: "Jewellery par zakat lagti hai Pakistan mein?",
      a: "Hanafi madhab: personal use ki gold/silver jewellery par bhi zakat lagti hai. Shafi'i, Maliki, Hanbali: personal-use jewellery exempt hai (unless excessive quantity). Apne local mufti se madhab-specific ruling confirm karen.",
    },
    {
      q: "Zakat kaha diya jata hai?",
      a: "Quran (9:60) mein 8 categories mention hain — fuqara (poor), masakin (needy), zakat collectors, new Muslims, riqab (freeing slaves), gharimin (debtors), fi sabilillah (Allah's cause), ibn as-sabil (travellers). Pakistan mein registered charities: Edhi, Al-Khidmat, Saylani, Alkhidmat Welfare aksar zakat-eligible programs run karte hain.",
    },
  ];

  const calcSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Pakistan Zakat Nisab Calculator",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any",
    url: CANONICAL,
    offers: { "@type": "Offer", price: 0, priceCurrency: "PKR" },
  };

  return (
    <Layout>
      <section className="mx-auto max-w-3xl px-4 py-8">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(calcSchema) }}
        />
        <PakistanBreadcrumbSchema
          items={[
            { name: "Pakistan", href: "https://drhint.com/pakistan" },
            { name: "Zakat Nisab", href: CANONICAL },
          ]}
        />

        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> › Zakat Nisab
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold">
          Zakat Nisab Pakistan — Live PKR Threshold
        </h1>
        <p className="mt-2 text-muted-foreground">
          Live gold (7.5 tola) and silver (52.5 tola) nisab thresholds in PKR,
          derived from today's international spot price + USD/PKR rate.
          2.5% zakat calculator built-in.
        </p>
        <div className="mt-3">
          <LiveBadge />
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Gold nisab (7.5 tola)
            </p>
            <p className="mt-2 text-2xl font-bold tabular-nums">
              {formatMoney(goldNisabPkr, "PKR")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              87.48 g × 24K per-gram rate
            </p>
          </div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Silver nisab (52.5 tola)
            </p>
            <p className="mt-2 text-2xl font-bold tabular-nums">
              {formatMoney(silverNisabPkr, "PKR")}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              612.35 g × estimated silver rate*
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <strong className="text-foreground">Effective nisab:</strong>{" "}
          {formatMoney(effectiveNisab, "PKR")} — the lower of the two, as
          preferred by most contemporary scholars (protects the poor).
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Calculate your zakat</h2>
          <label className="mt-3 block text-sm">
            <span className="mb-1 block font-medium">
              Total zakatable wealth (PKR)
            </span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="1000"
              value={wealth}
              onChange={(e) => setWealth(e.target.value)}
              placeholder="e.g. 500000"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 tabular-nums"
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Cash + bank + gold/silver value + business inventory + stocks.
              Exclude primary home, personal car, daily-use items.
            </span>
          </label>

          <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4 text-sm">
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">Your wealth</span>
              <span className="font-semibold tabular-nums">
                {formatMoney(wealthNum, "PKR")}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">Effective nisab</span>
              <span className="tabular-nums">{formatMoney(effectiveNisab, "PKR")}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-muted-foreground">Qualifies for zakat?</span>
              <span
                className={`font-semibold ${qualifies ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`}
              >
                {qualifies ? "Yes" : "No — below nisab"}
              </span>
            </div>
            <div className="mt-2 flex justify-between border-t border-border pt-2 text-lg">
              <span className="font-semibold">Zakat due (2.5%)</span>
              <span className="font-bold text-primary tabular-nums">
                {formatMoney(zakatDue, "PKR")}
              </span>
            </div>
          </div>
        </div>

        <article className="prose prose-sm max-w-none mt-8 text-foreground/90">
          <h2 className="text-xl font-bold">
            How nisab works — the classical fiqh position
          </h2>
          <p>
            Nisab wo minimum wealth threshold hai jis par zakat farz hoti hai.
            Prophet ﷺ ne do reference set kiye: <strong>7.5 tola gold</strong>{" "}
            (≈87.48 g of 24K) aur <strong>52.5 tola silver</strong> (≈612.35 g).
            Aaj ke daur mein silver nisab bahut kam PKR ki value banata hai
            kyunki historical gold:silver ratio 10:1 tha, lekin ab ~80:1 hai —
            majority ulema silver nisab prefer karte hain taake zyada log
            zakat pay karen aur zyada log qualify karen zakat receive karne ke
            liye.
          </p>
          <p>
            <strong>Zakat rate:</strong> 2.5% of total zakatable wealth held
            continuously for one lunar year (Hijri calendar). Zakatable wealth
            includes cash, bank deposits, savings, business inventory, gold &
            silver (personal-use jewellery treatment varies by madhab), stocks,
            mutual funds, aur receivable loans. Exempt: primary residence,
            personal car, household items.
          </p>
          <p className="text-xs text-muted-foreground">
            *Silver spot approximation: this page fetches gold spot directly
            from international sources. Silver is estimated at
            gold_spot ÷ 80 (long-run ratio). Real-time silver may vary
            2–5%. For exact silver-based nisab confirm today's silver rate
            from your local sarafa bazaar.
          </p>
          <p className="text-xs text-muted-foreground">
            This tool provides an estimate. Consult a qualified mufti for
            madhab-specific rulings and complex situations (business zakat,
            long-term investments, agricultural produce).
          </p>
        </article>

        <FaqSection faqs={faqs} title="Zakat & Nisab — FAQs (Pakistan)" />

        <PakistanNavigator exclude="/pakistan/zakat-nisab" />
      </section>
    </Layout>
  );
}